import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';

import './tooltipCss.css';

export interface TooltipV2Props {
    children: React.ReactNode;
    content: string;
    maxWidth?: string;
    isHeading?: boolean;
}

export const testId = 'tooltip';

const SHOW_DELAY_MS = 500;

const DEFAULT_TOOLTIP_WIDTH_PX = 300;

/** Fixed tooltip width for layout; supports `300px`, `50%` (of viewport), or plain number. */
export function tooltipWidthPx(
    maxWidth: string | undefined,
    viewportInnerWidth: number,
    horizontalPadding: number
): number {
    const cap = Math.max(0, viewportInnerWidth - horizontalPadding * 2);
    if (!maxWidth?.trim()) {
        return Math.min(DEFAULT_TOOLTIP_WIDTH_PX, cap);
    }
    const trimmed = maxWidth.trim();
    if (trimmed.endsWith('%')) {
        const pct = parseFloat(trimmed);
        if (!Number.isFinite(pct) || pct <= 0) {
            return Math.min(DEFAULT_TOOLTIP_WIDTH_PX, cap);
        }
        return Math.min(Math.round((viewportInnerWidth * pct) / 100), cap);
    }
    const px = parseInt(trimmed.replace(/px/gi, '').trim(), 10);
    if (!Number.isFinite(px) || px <= 0) {
        return Math.min(DEFAULT_TOOLTIP_WIDTH_PX, cap);
    }
    return Math.min(px, cap);
}

export function Tooltip({
    children,
    content,
    maxWidth,
    isHeading = false,
}: TooltipV2Props): JSX.Element {
    const containerRef = useRef<HTMLDivElement>(null);
    const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [visible, setVisible] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0, bottom: 0 });

    useEffect(() => {
        return () => {
            if (showTimeoutRef.current) {
                clearTimeout(showTimeoutRef.current);
            }
        };
    }, []);

    const handleMouseEnter = useCallback(() => {
        if (!containerRef.current || content === '') return;
        if (showTimeoutRef.current) {
            clearTimeout(showTimeoutRef.current);
        }
        showTimeoutRef.current = setTimeout(() => {
            showTimeoutRef.current = null;
            const el = containerRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            setPosition({
                top: rect.top + window.scrollY,
                left: rect.left + rect.width / 2 + window.scrollX,
                bottom: rect.bottom + window.scrollY,
            });
            setVisible(true);
        }, SHOW_DELAY_MS);
    }, [content]);

    const handleMouseLeave = useCallback(() => {
        if (showTimeoutRef.current) {
            clearTimeout(showTimeoutRef.current);
            showTimeoutRef.current = null;
        }
        setVisible(false);
    }, []);

    const hasContent = content !== '';

    return (
        <div
            ref={containerRef}
            data-testid={testId}
            id={isHeading ? 'tooltipHeading' : ''}
            style={{ width: '100%' }}
            onMouseEnter={hasContent ? handleMouseEnter : undefined}
            onMouseLeave={hasContent ? handleMouseLeave : undefined}
        >
            {children}
            {hasContent &&
                visible &&
                createPortal(
                    <TooltipBubble
                        content={content}
                        maxWidth={maxWidth}
                        top={position.top}
                        left={position.left}
                        bottom={position.bottom}
                    />,
                    document.body
                )}
        </div>
    );
}

export function TooltipBubble({
    content,
    maxWidth,
    top,
    left,
    bottom,
}: {
    content: string;
    maxWidth?: string;
    /** Document-space top of the trigger. */
    top: number;
    /** Document-space horizontal centre of the trigger. */
    left: number;
    /** Document-space bottom of the trigger. Enables flipping below. */
    bottom?: number;
}): JSX.Element {
    const bubbleRef = useRef<HTMLDivElement>(null);
    const arrowSize = 8;
    const padding = 8;

    // Config-driven tooltips join several option definitions with newlines.
    const lines = content.split('\n').filter((line) => line.trim() !== '');

    // Measured before paint so the bubble never renders in the wrong place.
    const [height, setHeight] = useState<number | null>(null);

    useLayoutEffect(() => {
        const measured = bubbleRef.current?.getBoundingClientRect().height;
        if (measured && measured !== height) setHeight(measured);
    });

    const viewportWidth = window.innerWidth;
    const bubbleWidth = tooltipWidthPx(maxWidth, viewportWidth, padding);

    let adjustedLeft = left;
    const estimatedWidth = bubbleWidth;

    if (adjustedLeft + estimatedWidth / 2 > viewportWidth - padding) {
        adjustedLeft = viewportWidth - padding - estimatedWidth / 2;
    }
    if (adjustedLeft - estimatedWidth / 2 < padding) {
        adjustedLeft = padding + estimatedWidth / 2;
    }

    // Flip below the trigger when there isn't room above it. Without this the
    // bubble is clipped by the top of the viewport and cannot be read.
    const triggerTopInViewport = top - window.scrollY;
    const spaceAbove = triggerTopInViewport - arrowSize - padding;
    const flipBelow = height !== null && height > spaceAbove;
    const triggerBottom = bottom ?? top;

    const bubbleStyle: React.CSSProperties = {
        position: 'absolute',
        top: flipBelow ? triggerBottom + arrowSize - 1 : top - arrowSize + 1,
        left: adjustedLeft,
        transform: flipBelow
            ? 'translateX(-50%)'
            : 'translateX(-50%) translateY(-100%)',
        width: bubbleWidth,
        zIndex: 9999,
        // A centred glossary of several options is hard to scan; left-align it.
        textAlign: lines.length > 1 ? 'left' : 'center',
        // Hide only for the first measuring pass.
        visibility: height === null ? 'hidden' : 'visible',
    };

    // Arrow always points to the centre of the trigger, even when the bubble
    // shifts sideways to stay within the viewport.
    const arrowLeft = Math.max(
        adjustedLeft - estimatedWidth / 2 + arrowSize * 2,
        Math.min(left, adjustedLeft + estimatedWidth / 2 - arrowSize * 2)
    );

    const arrowStyle: React.CSSProperties = {
        position: 'absolute',
        top: flipBelow ? triggerBottom - arrowSize : top - arrowSize,
        left: arrowLeft,
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
        border: `${arrowSize}px solid transparent`,
        // Both are always set. Omitting one lets React clear it back to the
        // Tailwind base border colour when the bubble flips, which draws a
        // grey wedge on the unused side.
        borderTopColor: flipBelow ? 'transparent' : 'rgba(51, 51, 51, 0.9)',
        borderBottomColor: flipBelow ? 'rgba(51, 51, 51, 0.9)' : 'transparent',
        zIndex: 9999,
        visibility: height === null ? 'hidden' : 'visible',
    };

    return (
        <>
            <div ref={bubbleRef} className='tooltipBubble' style={bubbleStyle}>
                {lines.length > 1
                    ? lines.map((line, index) => (
                          <TooltipLine
                              key={index}
                              line={line}
                              isLast={index === lines.length - 1}
                          />
                      ))
                    : content}
            </div>
            <div style={arrowStyle} />
        </>
    );
}

/**
 * One row of a multi-line tooltip. Config copy is written as
 * "Option name: what it means", so the name is emphasised to make the
 * list scannable.
 */
function TooltipLine({
    line,
    isLast,
}: {
    line: string;
    isLast: boolean;
}): JSX.Element {
    const separator = line.indexOf(':');
    const style: React.CSSProperties = { marginBottom: isLast ? 0 : '0.35rem' };

    if (separator === -1) {
        return <div style={style}>{line}</div>;
    }

    return (
        <div style={style}>
            <strong>{line.slice(0, separator + 1)}</strong>
            {line.slice(separator + 1)}
        </div>
    );
}
